import React, { useState, useEffect } from "react";
import "./Managezone.css";
import TextField from "@material-ui/core/TextField";
import axios from "axios";
import {
  Autocomplete,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CommonButton from "../../../Componants/Common/Button";
import ExportToExcel from "../../../Componants/Common/ExportToExcel";
import Paginate from "../../../Componants/Common/Paginate";
import HeaderNavigation from "../../../Componants/Common/header Navigation/HeaderNavigation";
import activeIcon from "../../../Assests/activeIcon.svg";
import inactiveIcon from "../../../Assests/inactiveIcon.svg";
import editIcon from "../../../Assests/editIcon.svg";
import Subheader from "../../../Componants/Common/Subheader";
import config from "../../../Componants/Common/config";
import {
  getCountryList,
  getZoneList,
  createZone,
  updateZoneStatus,
  updateZone,
  exportZoneList,
} from "../../../API Service/apiService";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Constants } from "../../../constants/Constants";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../../../Componants/Common/Loader";
import FileSaver from "file-saver";

const zoneHeaders = [
  { label: "S. No.", key: "serialNumber" },
  { label: "Country", key: "countryName" },
  { label: "Zone", key: "zoneName" },
];

const Managezone = () => {
  const pageSize = config.pageSize;
  const [page, setPage] = useState(1);
  const [zones, setZones] = useState([]);
  const [countries, setCountries] = useState([]);
  const [editIndex, setEditIndex] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zoneId, setZoneId] = useState("");
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [zoneDropdown, setZoneDropdown] = useState([]);
  const [dataSize, setDataSize] = useState(pageSize);

  const [searchParams, setSearchParams] = useState({
    countryId: "",
    zoneName: "",
    page: page,
    limit: pageSize,
  });

  const [totalRecords, setTotalRecords] = useState(1);

  const [filteredZones, setFilteredZones] = useState([]);

  const validationSchema = Yup.object({
    countryId: Yup.string().required("Country is required"),
    zoneName: Yup.string()
      .required("Zone name is required")
      .min(2, "Zone name must be at least 2 characters")
      .max(50, "Zone name must be at most 50 characters")
      .matches(
        /^[A-Za-z\s-]+$/,
        "Only alphabets, spaces and hyphens are allowed"
      ),
  });

  const formik = useFormik({
    initialValues: {
      countryId: "",
      zoneName: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (editIndex) {
          const editBody = {
            _id: zoneId,
            countryId: values.countryId,
            zoneName: values.zoneName,
          };
          const response = await updateZone(editBody);
          if (response.status === "success") {
            toast.success(response.message);
            await Promise.all([
              fetchZones(),
              fetchCountries()
            ]);
            formik.resetForm();
            setEditIndex(false);
          }
        } else {
          const response = await createZone({
            countryId: values.countryId,
            zoneName: values.zoneName,
          });
          if (response.status === "success") {
            toast.success(response.message);
            await Promise.all([
              fetchZones(),
              fetchCountries()
            ]);
            formik.resetForm();
          }
        }
      } catch (error) {
        toast.error(`Message: ${error.response.data.message}`);
      } finally {
        setLoading(false);
      }
    },
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams((prevParams) => ({
      ...prevParams,
      page: newPage.toString(),
    }));
  };

  useEffect(() => {
    fetchCountries();
    fetchZones();
  }, [page]);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await getCountryList();
      setCountries(response.data.countries);
      setCountryDropdown(response.data.countries);
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Failed to fetch countries");
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async (params = searchParams) => {
    try {
      setLoading(true);
      const response = await getZoneList(params);
      setZones(response.data.zones);
      setTotalRecords(response.data.totalZones);
    } catch (error) {
      console.error("Error fetching zones:", error);
      toast.error("Failed to fetch zones");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (index) => {
    const zoneToEdit = zones[index];
    setZoneId(zoneToEdit._id);
    formik.setValues({
      countryId: zoneToEdit.countryId,
      zoneName: zoneToEdit.zoneName,
    });
    setEditIndex(true);
  };

  const handleStatus = async (index, zoneId) => {
    try {
      setLoading(true);
      const response = await updateZoneStatus({ id: zoneId });
      if (response.status === "success") {
        toast.success("Status Updated Successfully");
        await Promise.all([
          fetchZones(),
          fetchCountries()
        ]);
      }
    } catch (error) {
      console.log(`Error updating status ${error}`);
      toast.error(`Failed to update status`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setPage(1);
    setSearchParams((prevParams) => ({
      ...prevParams,
      page: 1,
    }));
    await Promise.all([
      fetchZones(searchParams),
      fetchCountries()
    ]);
  };

  const handleCancel = async () => {
    setPage(1);
    const updatedParams = {
      ...searchParams,
      page: 1,
      countryId: "",
      zoneName: "",
    };
    setSearchParams(updatedParams);
    setFilteredZones([]);
    await Promise.all([
      fetchZones(updatedParams),
      fetchCountries()
    ]);
    formik.resetForm();
  };

  const startingSerialNumber = (page - 1) * pageSize + 1;

  const handleCountryChange = async (e, newValue) => {
    const countryId = newValue ? newValue._id : "";
    setSearchParams((prevParams) => ({
      ...prevParams,
      countryId,
      zoneName: "",
    }));
    
    if (countryId) {
      try {
        setLoading(true);
        const response = await getZoneList({ countryId });
        setFilteredZones(response.data.zones || []);
      } catch (error) {
        console.error("Error fetching zones for country:", error);
        toast.error("Failed to fetch zones for selected country");
        setFilteredZones([]);
      } finally {
        setLoading(false);
      }
    } else {
      setFilteredZones([]);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      const response = await exportZoneList(); // Assuming exportZoneList is a function in apiService.js

      if (response) {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, "zones.xlsx");
        toast.success("File downloaded successfully");
      } else {
        throw new Error("No data received for export.");
      }
    } catch (error) {
      console.error("Error exporting zone list:", error);
      toast.error(` ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="managezone-container">
      <Toaster />
      {loading && <Loader />}
      <HeaderNavigation value={"Location > Zone"} location={"/setting"} />

      <div className="autocompleteform-managezone">
        <Subheader heading={"Add Zone"} />
        <div className="textbox-main-Managezone">
          <div className="line-managezone">
            <div className="textinput-managezone">
              <Autocomplete
                id="country-search"
                options={countries}
                getOptionLabel={(option) => option.countryName}
                value={
                  countries.find(
                    (country) => country._id === formik.values.countryId
                  ) || null
                }
                onChange={(e, newValue) => {
                  formik.setFieldValue(
                    "countryId",
                    newValue ? newValue._id : ""
                  );
                }}
                renderInput={(params) => (
                  <TextField
                  error={
                    formik.touched.countryId &&
                    Boolean(formik.errors.countryId)
                  }
                  helperText={
                    formik.touched.countryId && formik.errors.countryId
                  }
                  InputLabelProps={{
                    required: true,
                    shrink: true,
                  }}
                    {...params}
                    label="Country"
                    variant="standard"
                    style={{ width: "100%" }}
                  />
                )}
              />
            </div>

            <div className="textinput-managezone">
              <TextField
                style={{ width: "100%" }}
                id="zoneName"
                label="Zone"
                variant="standard"
                value={formik.values.zoneName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.zoneName && Boolean(formik.errors.zoneName)
                }
                helperText={formik.touched.zoneName && formik.errors.zoneName}
                InputLabelProps={{
                  required: true,
                }}
              />
            </div>
          </div>

          <div className="button-managezone">
            <span className="buttons-managezone-span">
              <CommonButton
                name={editIndex ? Constants.update : Constants.create}
                handleOnClick={formik.handleSubmit}
              />
            </span>
            <span className="buttons-managezone-span">
              <CommonButton
                name={Constants.cancel}
                handleOnClick={() => {
                  formik.resetForm();
                  setEditIndex(false);
                }}
              />
            </span>
          </div>
        </div>
      </div>

      <div className="autocompleteform-managezone">
        <Subheader heading={"List View"} />
        <div className="line-managezone">
          <div className="textinput-managezone">
            <Autocomplete
              id="country-search"
              options={countryDropdown}
              getOptionLabel={(option) => option.countryName}
              value={
                countryDropdown.find(
                  (country) => country._id === searchParams.countryId
                ) || null
              }
              onChange={handleCountryChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  variant="standard"
                  style={{ width: "100%" }}
                />
              )}
            />
          </div>

          <div className="textinput-managezone">
            <Autocomplete
              id="zone-search"
              options={filteredZones}
              getOptionLabel={(option) => option.zoneName}
              value={
                filteredZones.find((zone) => zone.zoneName === searchParams.zoneName) ||
                null
              }
              onChange={(e, newValue) => {
                setSearchParams((prevParams) => ({
                  ...prevParams,
                  zoneName: newValue ? newValue.zoneName : "",
                }));
              }}
              disabled={!searchParams.countryId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Zone"
                  variant="standard"
                  style={{ width: "100%" }}
                  // helperText={!searchParams.countryId ? "Please select a country first" : ""}
                />
              )}
            />
          </div>

          <div className="button-managezone" style={{ marginTop: "0" }}>
            <span className="buttons-managezone-span">
              <CommonButton
                name={Constants.search}
                handleOnClick={handleSearch}
              />
            </span>
            <span className="buttons-managezone-span">
              <CommonButton
                name={Constants.cancel}
                handleOnClick={handleCancel}
              />
            </span>
          </div>
        </div>

        <div className="table-Managezone">
          <div className="excelexport-Managecity">
            <span className="buttons-Managecity-span">
              <ExportToExcel onClick={handleExportToExcel} />
            </span>
          </div>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#fff" }} align="left">
                    S. No.
                  </TableCell>
                  <TableCell sx={{ color: "#fff" }} align="center">
                    Country
                  </TableCell>
                  <TableCell sx={{ color: "#fff" }} align="center">
                    Zone
                  </TableCell>
                  <TableCell sx={{ color: "#fff" }} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No Data Available
                    </TableCell>
                  </TableRow>
                ) : (
                  zones.map((zone, index) => (
                    <TableRow key={zone._id}>
                      <TableCell align="left">
                        {startingSerialNumber + index}
                      </TableCell>
                      <TableCell align="center">{zone.countryName}</TableCell>
                      <TableCell align="center">{zone.zoneName}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "flex-end",
                        }}
                      >
                        <IconButton
                          onClick={() => handleStatus(index, zone._id)}
                          sx={{
                            outline: "none",
                            "&:focus": { outline: "none" },
                          }}
                        >
                          <img
                            src={
                              zone.active === false ? inactiveIcon : activeIcon
                            }
                            alt="active"
                            height={"20px"}
                            width={"20px"}
                          />
                        </IconButton>

                        <IconButton
                          onClick={() => handleEdit(index)}
                          sx={{
                            outline: "none",
                            "&:focus": { outline: "none" },
                          }}
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            height={"20px"}
                            width={"20px"}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div className="pagination-container">
          <Paginate
            page={page}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            dataSize={dataSize}
          />
        </div>
      </div>
    </div>
  );
};

export default Managezone;
